<script setup>
import Loading from '../../Loading.vue';
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import XlButton from '../../xl/XlButton.vue';
import CommsInfoBanner from '../../communications/CommsInfoBanner.vue';
import { COMMS_HINTS } from './hints';
import XlCard from '../../xl/XlCard.vue';
import XlListRow from '../../xl/XlListRow.vue';
import XlTag from '../../xl/XlTag.vue';
import XlStatusIcon from '../../xl/XlStatusIcon.vue';
import TemplateEditor from '../../communications/TemplateEditor.vue';
import { useSelection } from '../../communications/useSelection';
import { createTemplate, deleteTemplate, listTemplates, updateTemplate } from '../../../api/templates';
import { channelLabel } from '../../communications/channelHelpers';
import { useCommunicationsStore } from '../../../stores/communications';

import XlField from '../../xl/XlField.vue';

const store = useCommunicationsStore();
const { selectedIds, isSelected, toggle, clear, selectedCount } = useSelection();

const templates = ref([]);
const loadingList = ref(true);
const listError = ref('');

const isFormOpen = ref(false);
const editingTemplate = ref(null);
const saving = ref(false);
const saveError = ref('');

const deleteWarning = reactive({ templateId: null, sequences: [] });

const standardTemplates = computed(() => templates.value.filter((template) => template.is_standard));
const customTemplates = computed(() => templates.value.filter((template) => !template.is_standard));
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../../stores/auth';
import { canAssignBrokers } from '../../../helpers/roles';

const router = useRouter();
const auth = useAuthStore();
const isAgency = computed(() => canAssignBrokers(auth.userData));

// The server says per template whether this viewer may change it: the owner
// and hub staff may change any, a broker only the custom ones they wrote.
const mayChange = (template) => template.can_edit ?? isAgency.value;

const actionFor = (template) => (mayChange(template) ? 'Edit' : 'Use');

// A broker can tick only her own rows; the others keep the checkbox's room so
// every icon in the column lines up.
const anyChangeable = computed(() => templates.value.some(mayChange));

const origin = (template) => (template.is_standard ? 'Standard template' : 'Created by your team');

const act = (template) => {
  if (actionFor(template) === 'Edit') return openEdit(template);

  return router.push({ query: { tab: 'send', template: template.id } });
};

const templateGroups = computed(() => [
  { label: 'Standard', items: standardTemplates.value },
  { label: 'Custom', items: customTemplates.value },
]);

const fetchList = async () => {
  loadingList.value = true;
  listError.value = '';
  try {
    // GET also seeds each hub's standard templates on its first call
    // (TemplateController::index) — idempotent, so refetching here is safe.
    templates.value = await listTemplates();
  } catch (e) {
    listError.value = 'Could not load templates.';
  } finally {
    loadingList.value = false;
  }
};

const openNew = () => {
  clear();
  editingTemplate.value = null;
  saveError.value = '';
  isFormOpen.value = true;
};

const openEdit = (template) => {
  clear();
  editingTemplate.value = template;
  saveError.value = '';
  isFormOpen.value = true;
};

const closeForm = () => {
  isFormOpen.value = false;
};

const save = async (payload) => {
  saving.value = true;
  saveError.value = '';
  try {
    if (editingTemplate.value) {
      await updateTemplate(editingTemplate.value.id, payload);
    } else {
      await createTemplate(payload);
    }
    isFormOpen.value = false;
    window.loadNotification?.({ message: 'Template saved' });
    await fetchList();
  } catch (e) {
    saveError.value = 'Could not save this template. Please try again.';
  } finally {
    saving.value = false;
  }
};

const requestDelete = async (template) => {
  try {
    const result = await deleteTemplate(template.id);
    const referencing = result?.referencing_sequences || [];
    if (referencing.length > 0) {
      deleteWarning.templateId = template.id;
      deleteWarning.sequences = referencing;
      return;
    }
    templates.value = templates.value.filter((t) => t.id !== template.id);
  } catch (e) {
    listError.value = 'Could not delete this template.';
  }
};

const deleteSelected = async () => {
  for (const id of [...selectedIds.value]) {
    await requestDelete(templates.value.find((template) => template.id === id));
  }
  clear();
};

watch(
  selectedCount,
  (count) => {
    store.setToolbar({ deleteDisabled: count === 0, onDelete: deleteSelected });
  },
  { immediate: true }
);

const dismissDeleteWarning = () => {
  deleteWarning.templateId = null;
  deleteWarning.sequences = [];
};

onMounted(() => {
  store.fetchSettings();
  fetchList();
});
// The tabs above belong to the parent, so it is told to stand down while
// this editor owns the screen.
watch(isFormOpen, (open) => store.setEditing(open));

onBeforeUnmount(() => {
  store.clearToolbar();
  store.setEditing(false);
});
</script>

<template>
  <div class="templates-tab">
    <div v-if="!isFormOpen">
      <CommsInfoBanner :text="COMMS_HINTS.templates">
        <slot name="actions" />
        <XlButton data-testid="new-template" @click="openNew">
          <template #icon>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 4.5v11M4.5 10h11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
          </template>
          New template
        </XlButton>
      </CommsInfoBanner>

      <div
        v-if="deleteWarning.templateId"
        class="templates-tab__alert templates-tab__delete-warning"
        role="alert"
        data-testid="delete-warning"
      >
        <p>This template can't be deleted — it's still used by {{ deleteWarning.sequences.join(', ') }}.</p>
        <XlButton variant="ghost" size="sm" @click="dismissDeleteWarning">OK</XlButton>
      </div>

      <p v-if="listError" role="alert" class="templates-tab__alert">{{ listError }}</p>
      <Loading v-else-if="loadingList" variant="rows" :rows="4" data-testid="templates-loading" />
      <p v-else-if="!templates.length" class="templates-tab__empty">
        No templates yet. Create one to get started.
      </p>

      <template v-else>
        <template v-for="group in templateGroups" :key="group.label">
          <div v-if="group.items.length" class="templates-tab__group" data-testid="template-group">
            <h2>{{ group.label }}</h2>
            <span class="templates-tab__rule" aria-hidden="true" />
          </div>

          <XlCard v-if="group.items.length" class="templates-tab__list">
            <XlListRow
              v-for="template in group.items"
              :key="template.id"
              data-testid="template-row"
              :class="{ 'templates-tab__row--selected': isSelected(template.id) }"
            >
              <template #leading>
                <XlField
                  v-if="mayChange(template)"
                  type="checkbox"
                  :name="`template-${template.id}`"
                  :aria-label="`Select ${template.name}`"
                  data-testid="template-row-checkbox"
                  :model-value="isSelected(template.id)"
                  @update:model-value="(checked) => toggle(template.id, checked)"
                />
                <span v-else-if="anyChangeable" class="templates-tab__check-placeholder" aria-hidden="true">
                  <XlField type="checkbox" disabled />
                </span>
                <XlStatusIcon tone="brand"><svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg></XlStatusIcon>
              </template>

              <template #title>{{ template.name }}</template>
              <template #subtitle>
                <span data-testid="template-row-meta">{{ origin(template) }}</span>
              </template>

              <template #trailing>
                <div class="templates-tab__chips">
                  <XlTag
                    v-for="channel in template.channels"
                    :key="channel"
                    :tone="channel === 'email' ? 'brand' : 'success'"
                    data-testid="template-row-chip"
                  >{{ channelLabel(channel) }}</XlTag>
                </div>

                <XlButton
                  data-testid="template-row-edit"
                  variant="ghost"
                  size="sm"
                  @click="act(template)"
                >{{ actionFor(template) }}</XlButton>
              </template>
            </XlListRow>
          </XlCard>
        </template>
      </template>
    </div>

    <div v-else class="templates-tab__form">
      <p v-if="saveError" role="alert" class="templates-tab__alert" data-testid="save-error">{{ saveError }}</p>
      <TemplateEditor
        :template="editingTemplate"
        :standard-templates="standardTemplates"
        :sender="store.settings"
        @save="save"
        @cancel="closeForm"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.templates-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--xl-color-primary-text);

  &__group {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin: 34px 0 14px;

    h2 {
      margin: 0;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--xl-color-outline-variant);
    }
  }

  &__rule {
    flex: 1;
    height: 1px;
    background: var(--xl-color-border);
  }

  &__list { padding: 8px 22px; overflow: hidden; }

  &__chips { display: flex; align-items: center; gap: 8px; }

  // The fill reaches the card's edges while the row's own text stays where
  // every unselected row has it.
  &__row--selected {
    margin-inline: -22px;
    padding-inline: 22px;
    background: var(--xl-color-primary-container);

    .xl-tag, .xl-status-icon { background: #fff; }
  }

  &__empty {
    margin: 0;
    font-size: 13.5px;
    color: var(--xl-color-outline);
  }

  &__alert {
    margin: 0;
    padding: 11px 14px;
    border-radius: 12px;
    background: var(--xl-color-error-container);
    color: var(--xl-color-error);
    font-size: 13px;
    line-height: 1.55;
  }

  &__delete-warning {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;

    p { margin: 0; }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
.templates-tab__check-placeholder { visibility: hidden; }
</style>
